// src/features/works/WorkProfile.jsx - CON NAVEGACIÓN MEJORADA QUE MANTIENE FILTROS
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Tabs, Tab, Spinner, Badge, Alert } from 'react-bootstrap';
import { FaEdit, FaArrowLeft, FaTrashAlt, FaEye, FaLock, FaFilter } from 'react-icons/fa';
import dayjs from 'dayjs';

// Hooks
import { useNavigationHistory } from '../../hooks/useNavigationHistory';

// Componentes de sección
import PeopleSection from './components/PeopleSection';
import ToolsSection from './components/ToolsSection';
import DocumentsSection from './components/DocumentsSection';

// Componentes generales
import CustomSnackbar from '../../components/CustomSnackbar';

// Servicios y utilidades
import WorksService from './WorksService';
import { FORMATO_FECHA } from '../../utils/constants';

/**
 * Componente para mostrar el perfil detallado de una obra - CON NAVEGACIÓN MEJORADA
 */
function WorkProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Estados principales
  const [work, setWork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('personas');
  const [snackbar, setSnackbar] = useState({ open: false, type: '', message: '' });
  
  // Estado para recargar datos
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // ✅ USAR NAVEGACIÓN SIMPLIFICADA (sin duplicar funcionalidad de filtros)
  const { goBack, getFilterInfo } = useNavigationHistory();

  // ✅ OBTENER INFORMACIÓN DE FILTROS PARA MOSTRAR INDICADORES
  const filterInfo = getFilterInfo('/works');

  // Función para cargar los datos de la obra
  const loadWorkData = useCallback(async () => {
    try {
      setLoading(true);
      const workData = await WorksService.getWork(id);
      setWork(workData);
    } catch (error) {
      console.error('Error al cargar los datos de la obra:', error);
      setSnackbar({
        open: true,
        type: 'error',
        message: 'Error al cargar los datos de la obra'
      });
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Cargar datos de la obra
  useEffect(() => {
    loadWorkData();
  }, [loadWorkData, refreshTrigger]);

  // Función para mostrar mensajes en snackbar
  const showSnackbar = useCallback((type, message) => {
    setSnackbar({
      open: true,
      type,
      message
    });
  }, []);

  // Función para recargar datos cuando hay cambios (solo si no está finalizada)
  const handleDataChange = useCallback(() => {
    if (work && !work.deletedDate) {
      setRefreshTrigger(prev => prev + 1);
    }
  }, [work]);

  // Navegar a edición - CON RESTRICCIÓN
  const handleEditWork = () => {
    if (work.deletedDate) {
      setSnackbar({
        open: true,
        type: 'warning',
        message: 'No se puede editar una obra finalizada'
      });
      return;
    }
    navigate(`/works/${id}/edit`);
  };

  // Finalizar obra - CON RESTRICCIÓN
  const handleDeleteWork = async () => {
    if (work.deletedDate) {
      setSnackbar({
        open: true,
        type: 'warning',
        message: 'Esta obra ya está finalizada'
      });
      return;
    }

    if (window.confirm(`¿Está seguro que desea finalizar la obra "${work.name}"?`)) {
      try {
        // ✅ USAR EL NUEVO MÉTODO finalizeWork
        const result = await WorksService.finalizeWork(id);
        
        // Manejar éxito (códigos 2xx)
        if (result.statusCode >= 200 && result.statusCode < 300) {
          setSnackbar({
            open: true,
            type: 'success',
            message: 'Obra finalizada correctamente'
          });
          
          // Recargar datos para reflejar el cambio
          setTimeout(() => {
            loadWorkData();
          }, 1500);
        } else {
          // Manejar errores - usar el mensaje del API directamente
          const errorMessage = result.data?.message || `Error del servidor (${result.statusCode})`;
          
          setSnackbar({
            open: true,
            type: 'error',
            message: errorMessage
          });
        }
      } catch (error) {
        // Error inesperado en el cliente
        console.error('Error inesperado al finalizar obra:', error);
        setSnackbar({
          open: true,
          type: 'error',
          message: 'Error inesperado al finalizar la obra. Intente nuevamente.'
        });
      }
    }
  };

  // Cerrar snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Mostrar spinner durante la carga
  if (loading && !work) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando...</span>
        </Spinner>
      </Container>
    );
  }

  // Si no se encuentra la obra
  if (!work) {
    return (
      <Container className="text-center mt-5">
        <h3>No se encontró la obra solicitada</h3>
        <Button variant="primary" onClick={goBack} className="mt-3">
          Volver a la lista
        </Button>
      </Container>
    );
  }

  // Verificar si la obra está finalizada
  const isWorkFinalized = work.deletedDate !== null;

  return (
    <Container fluid className="p-0">
      {/* ✅ ALERTA PARA OBRAS FINALIZADAS */}
      {isWorkFinalized && (
        <Alert variant="warning" className="mx-3 mt-2 d-flex align-items-center">
          <FaLock className="me-2" />
          <div>
            <strong>Obra Finalizada:</strong> Esta obra fue finalizada el{' '}
            <strong>{dayjs(work.deletedDate).format(FORMATO_FECHA)}</strong>.
            No se pueden realizar modificaciones.
          </div>
        </Alert>
      )}

      {/* ✅ ALERTA INFORMATIVA SOBRE FILTROS GUARDADOS */}


      {/* Barra de acciones superior - CON BOTONES CONDICIONALES */}
      <div className="bg-light p-2 d-flex align-items-center">
        {/* ✅ BOTÓN VOLVER MEJORADO CON INDICADOR DE FILTROS */}
        <Button 
          variant="outline-secondary" 
          onClick={goBack}
          className="me-2"
          size="sm"
        >
          <FaArrowLeft className="me-1" /> 
          Volver
          {filterInfo.hasFilters && (
            <Badge bg="info" className="ms-1" style={{ fontSize: '0.6em' }}>
              {filterInfo.activeFiltersCount + (filterInfo.showFinalized ? 1 : 0)}
            </Badge>
          )}
        </Button>
        
        {/* ✅ BOTÓN DE EDICIÓN CONDICIONAL */}
        {isWorkFinalized ? (
          <Button 
            variant="outline-secondary" 
            onClick={handleEditWork}
            className="me-2"
            size="sm"
            disabled
          >
            <FaLock className="me-1" /> Obra Finalizada
          </Button>
        ) : (
          <Button 
            variant="outline-primary" 
            onClick={handleEditWork}
            className="me-2"
            size="sm"
          >
            <FaEdit className="me-1" /> Editar
          </Button>
        )}

        {/* ✅ BOTÓN DE FINALIZACIÓN CONDICIONAL */}
        {!isWorkFinalized && (
          <Button 
            variant="outline-danger" 
            onClick={handleDeleteWork}
            size="sm"
          >
            <FaTrashAlt className="me-1" /> Finalizar Obra
          </Button>
        )}
        
        {/* ✅ BADGE DE ESTADO */}
        <div className="ms-3">
          {isWorkFinalized ? (
            <Badge bg="secondary" className="fs-6 px-3 py-2">
              <FaLock className="me-1" /> Finalizada
            </Badge>
          ) : (
            <Badge bg="success" className="fs-6 px-3 py-2">
              <FaEye className="me-1" /> Activa
            </Badge>
          )}
        </div>
      </div>

      <Row className="m-0">
        {/* Columna izquierda - Información general */}
        <Col md={3} className="p-2">
          <Card>
            <Card.Header className="bg-light">
              <strong>Información General</strong>
            </Card.Header>
            <Card.Body>
              <Row className="mb-2">
                <Col xs={4} className="text-muted">Nombre:</Col>
                <Col xs={8}><strong>{work.name}</strong></Col>
              </Row>
              <Row className="mb-2">
                <Col xs={4} className="text-muted">Ubicación:</Col>
                <Col xs={8}>{work.location}</Col>
              </Row>
              <Row className="mb-2">
                <Col xs={4} className="text-muted">Fecha inicio:</Col>
                <Col xs={8}>{dayjs(work.startDate).format(FORMATO_FECHA)}</Col>
              </Row>
              {work.endDate && (
                <Row className="mb-2">
                  <Col xs={4} className="text-muted">Fecha fin:</Col>
                  <Col xs={8}>{dayjs(work.endDate).format(FORMATO_FECHA)}</Col>
                </Row>
              )}
              <Row className="mb-2">
                <Col xs={4} className="text-muted">Estado:</Col>
                <Col xs={8}>
                  {isWorkFinalized ? (
                    <Badge bg="secondary">Finalizada</Badge>
                  ) : (
                    <Badge bg="success">Activa</Badge>
                  )}
                </Col>
              </Row>
              {/* ✅ NUEVA FILA: Fecha de finalización */}
              {isWorkFinalized && (
                <Row className="mb-2">
                  <Col xs={4} className="text-muted">Finalizada:</Col>
                  <Col xs={8}>{dayjs(work.deletedDate).format(FORMATO_FECHA)}</Col>
                </Row>
              )}
              <Row className="mb-2">
                <Col xs={4} className="text-muted">Cliente:</Col>
                <Col xs={8}>
                  {work.customers && work.customers.length > 0 
                    ? work.customers[0].businessName || `${work.customers[0].firstName} ${work.customers[0].surname || ''}`
                    : 'No asignado'
                  }
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Observaciones */}
          <Card className="mt-3">
            <Card.Header className="bg-light">
              <strong>Observaciones</strong>
            </Card.Header>
            <Card.Body>
              {work.observations 
                ? <p>{work.observations}</p>
                : <p className="text-muted">No hay observaciones registradas</p>
              }
            </Card.Body>
          </Card>
        </Col>

        {/* Columna derecha - Tabs con secciones */}
        <Col md={9} className="p-2">
          <Card>
            <Card.Body className="d-flex flex-column" style={{ padding: '1rem' }}>
              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-3"
              >
                {/* Tab de personas asignadas */}
                <Tab eventKey="personas" title="Personas Asignadas">
                  <PeopleSection
                    workId={id}
                    persons={work.persons || []}
                    onDataChange={handleDataChange}
                    showSnackbar={showSnackbar}
                    isWorkFinalized={isWorkFinalized} // ✅ PASAR ESTADO DE FINALIZACIÓN
                  />
                </Tab>
                
                {/* Tab de herramientas asignadas */}
                <Tab eventKey="equipos" title="Herramientas Asignadas">
                  <ToolsSection
                    workId={id}
                    tools={work.tools || []}
                    onDataChange={handleDataChange}
                    showSnackbar={showSnackbar}
                    isWorkFinalized={isWorkFinalized} // ✅ PASAR ESTADO DE FINALIZACIÓN
                  />
                </Tab>
                
                {/* Tab de documentos */}
                <Tab eventKey="documentos" title="Documentos">
                  <DocumentsSection
                    workId={id}
                    documents={work.documents || []}
                    onDataChange={handleDataChange}
                    showSnackbar={showSnackbar}
                    isWorkFinalized={isWorkFinalized} // ✅ PASAR ESTADO DE FINALIZACIÓN
                  />
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      <CustomSnackbar
        open={snackbar.open}
        type={snackbar.type}
        message={snackbar.message}
        onClose={handleCloseSnackbar}
      />
    </Container>
  );
}

export default WorkProfile;